import { useEffect } from "react";
import { selectUserInformation } from "../../State/Slices/UserInformationSlice";
import { selectClientValue } from "../Slices/SelectedClientSlice";
import { useSelector } from "react-redux";

interface IPendoScriptProps {
  clientId: string;
  sub: string;
  // Optional: userInformation.email can be undefined before user info has
  // loaded (see escapeForInlineScript's ?? "" guard below).
  email?: string;
}

const pendoApiKey = "899791b9-7114-4498-6a77-b7b7baa0fa09";

export function usePendoInitialize() {
  const selectedClient = useSelector(selectClientValue);
  const userInformation = useSelector(selectUserInformation);
  const activateId = userInformation["http://nasrecruitment.io/activateId"];
  const isAuthenticated = !!activateId && !!selectedClient?.id;
  const isPendoLoaded =
    window.pendo !== undefined && pendo.isReady ? pendo.isReady() : false;

  useEffect(() => {
    const userAuthId = `auth0|${activateId}`;
    const clientId = selectedClient.id;
    const email = userInformation.email;

    async function changeClient() {
      await pendo.flushNow();
      pendo.identify({
        visitor: { id: userAuthId, email },
        account: { id: clientId },
      });
    }

    if (isAuthenticated) {
      const isScriptAdded = document.getElementById("pendo-base") !== null;

      if (!isScriptAdded) {
        addPendoScript({
          email,
          sub: userAuthId,
          clientId,
        });
      }

      if (isPendoLoaded) {
        changeClient();
      }
    }

    return () => {
      if (isPendoLoaded) {
        pendo.flushNow();
      }
    };
  }, [
    isAuthenticated,
    isPendoLoaded,
    selectedClient.id,
    activateId,
    userInformation.email,
  ]);
}

function addPendoScript(data: IPendoScriptProps) {
  if (!data.clientId || !data.sub) {
    return;
  }
  const script = document.createElement("script");
  script.id = "pendo-base";
  // textContent (unlike innerHTML) isn't HTML-parsed at all, so a literal
  // "</script" in an interpolated value can't prematurely terminate the tag.
  script.textContent = basePendoScript(data);
  document.body.appendChild(script);
}

// Values interpolated into an inline <script> string must be escaped as JS
// string literals (not just wrapped in quotes) so a crafted email/id can't
// break out of the literal and inject arbitrary script (CWE-79).
const escapeForInlineScript = (value?: string) =>
  JSON.stringify(value ?? "").replace(/<\/script/gi, "<\\/script");

const getInitializeScript = (data: IPendoScriptProps) => {
  const sub = escapeForInlineScript(data.sub);
  const email = escapeForInlineScript(data.email);
  const clientId = escapeForInlineScript(data.clientId);

  return window.parent.Cypress !== undefined
    ? `pendo.initialize({
    visitor: {id: ${sub}, email: ${email}},
    account: {id: ${clientId}},
    disableGuides: true
  });`
    : `pendo.initialize({
    visitor: {id: ${sub}, email: ${email}},
    account: {id: ${clientId}}
  });`;
};

function basePendoScript(data: IPendoScriptProps) {
  const initializeScript = getInitializeScript(data);
  return `
  (function(apiKey){
    (function(p,e,n,d,o){var v,w,x,y,z;o=p[d]=p[d]||{};o._q=o._q||[];
    v=['initialize','identify','updateOptions','pageLoad','track'];for(w=0,x=v.length;w<x;++w)(function(m){
        o[m]=o[m]||function(){o._q[m===v[0]?'unshift':'push']([m].concat([].slice.call(arguments,0)));};})(v[w]);
        y=e.createElement(n);y.async=!0;y.src='https://cdn.pendo.io/agent/static/'+apiKey+'/pendo.js';
        z=e.getElementsByTagName(n)[0];z.parentNode.insertBefore(y,z);})(window,document,'script','pendo');
        ${initializeScript}
  })('${pendoApiKey}');`;
}
